import * as FileSystem from 'expo-file-system/legacy';
import { decode as decodeBase64ArrayBuffer } from 'base64-arraybuffer';

import { databaseService } from '@/services/databaseService';
import type {
  RecoveryCaptureInput,
  RecoveryConsistentSnapshot,
  RecoveryMediaReader,
  RecoverySnapshotMediaSource,
  RecoverySnapshotProvider,
} from '@/types/recovery';
import { FileSystemRecoveryCaptureStore } from './FileSystemRecoveryCaptureStore';
import { createAppMultipartRecoveryCaptureAdapter } from './MultipartRecoveryCaptureAdapter';

interface RecoverySnapshotDatabase {
  captureRecoverySnapshot(input: RecoveryCaptureInput): Promise<RecoveryConsistentSnapshot>;
}

const RELATIVE_LOCAL_MEDIA_PREFIXES = [
  'checklist-photos/',
  'checklist-signatures/',
  'checklist-marcacoes/',
  'checklist-marcacao-bkg/',
] as const;

const resolveReadableUri = (sourceUri: string): string => {
  if (sourceUri.startsWith('file://') || sourceUri.startsWith('content://')) {
    return sourceUri;
  }
  if (sourceUri.startsWith('blob:')) {
    throw new Error('Midia blob nao pode ser preservada pelo filesystem nativo.');
  }
  if (
    RELATIVE_LOCAL_MEDIA_PREFIXES.some(prefix => sourceUri.startsWith(prefix)) &&
    !sourceUri.includes('..') &&
    FileSystem.documentDirectory
  ) {
    return `${FileSystem.documentDirectory}${sourceUri}`;
  }
  throw new Error('Referencia de midia local invalida para recovery.');
};

export class AppRecoverySnapshotProvider implements RecoverySnapshotProvider {
  constructor(private readonly database: RecoverySnapshotDatabase = databaseService) {}

  captureConsistentSnapshot(
    input: RecoveryCaptureInput
  ): Promise<RecoveryConsistentSnapshot> {
    return this.database.captureRecoverySnapshot(input);
  }
}

export class ExpoFileSystemRecoveryMediaReader implements RecoveryMediaReader {
  async readLocalBytes(source: RecoverySnapshotMediaSource): Promise<Uint8Array> {
    const uri = resolveReadableUri(source.sourceUri);
    let info: FileSystem.FileInfo;
    try {
      info = await FileSystem.getInfoAsync(uri);
    } catch {
      throw new Error(`Midia local indisponivel para recovery: ${source.mediaId}.`);
    }
    if (!info.exists || info.isDirectory) {
      throw new Error(`Midia local indisponivel para recovery: ${source.mediaId}.`);
    }

    let encoded: string;
    try {
      encoded = await FileSystem.readAsStringAsync(uri, {
        encoding: FileSystem.EncodingType.Base64,
      });
    } catch {
      throw new Error(`Falha ao ler midia local para recovery: ${source.mediaId}.`);
    }
    const bytes = new Uint8Array(decodeBase64ArrayBuffer(encoded));
    if (bytes.length === 0 || (typeof info.size === 'number' && info.size !== bytes.length)) {
      throw new Error(`Midia local inconsistente para recovery: ${source.mediaId}.`);
    }
    return bytes;
  }
}

export const createAppRecoveryCaptureDependencies = () => ({
  snapshotProvider: new AppRecoverySnapshotProvider(),
  mediaReader: new ExpoFileSystemRecoveryMediaReader(),
  store: new FileSystemRecoveryCaptureStore(),
});

export const createConfiguredAppMultipartRecoveryCaptureAdapter = () =>
  createAppMultipartRecoveryCaptureAdapter(createAppRecoveryCaptureDependencies());
